// src/features/streams/subaccounts/useSubAccountStreams.ts

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { LIVE_STATUS_POLL_INTERVAL_MS } from '@/lib/constants/stream';
import { StreamApi } from '../StreamApi';
import { StreamUtils } from '../StreamUtils';
import type { StreamViewData, RealtimeStreamData } from '@/lib/types/Stream';

/**
 * Whether the Sub-Account Streams view is available: master account selected,
 * subcustomerRead permission, and at least one sub-account exists.
 * Mirrors the backend authorization on media/subaccount-videostream
 * (canSubcustomerRead) — read-only monitoring does not require subcustomerWrite.
 */
export function useSubAccountStreamsAccess(): boolean {
  const { authData, selectedCustomer, subcustomers } = useAuth();
  return (
    !!selectedCustomer &&
    !selectedCustomer.cfg.parentCId &&
    !!authData?.cpuser.permissions.subcustomerRead &&
    subcustomers.length > 0
  );
}

/**
 * Fetches all active streams across all sub-accounts (consolidated endpoint,
 * scoped server-side to the login account's children).
 */
export function useSubAccountStreams(enabled: boolean) {
  const { authData } = useAuth();
  const loginCId = authData?.cpuser.core.cId;

  const query = useQuery({
    queryKey: MEDIA_QUERY_KEYS.subAccountStreams(loginCId ?? 0),
    queryFn: async () => {
      const result = await StreamApi.getSubAccountStreams();
      return result.data;
    },
    enabled: enabled && loginCId != null,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const streams: StreamViewData[] = useMemo(() => query.data ?? [], [query.data]);

  return {
    streams,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Polls realtime stream statuses across all sub-accounts.
 * Same cadence/semantics as useStreamLiveStatus; polling pauses when the
 * window loses focus and stops when no consumer is mounted.
 */
export function useSubAccountLiveStatus(enabled: boolean) {
  const { authData } = useAuth();
  const loginCId = authData?.cpuser.core.cId;

  const query = useQuery({
    queryKey: MEDIA_QUERY_KEYS.subAccountStreamLiveStatus(loginCId ?? 0),
    queryFn: () => StreamApi.getSubAccountRealtimeStreams(),
    enabled: enabled && loginCId != null,
    refetchInterval: LIVE_STATUS_POLL_INTERVAL_MS,
    staleTime: LIVE_STATUS_POLL_INTERVAL_MS - 1000,
    gcTime: 5 * 60 * 1000,
  });

  const liveStatusMap = useMemo(() => {
    if (!query.data) return new Map<number, RealtimeStreamData>();
    return StreamUtils.buildLiveStatusMap(query.data.data);
  }, [query.data]);

  return { liveStatusMap, isLoading: query.isLoading };
}
